import $ from 'jquery';
import EventListControls from './EventsListControls';
import Class from '../EventListingViewClasses';
import { makeNoResultsHtml, makeResultsDateRangeHeading, makeBusyIndicatorHtml } from '../htmlShared';
import { isBetweenDates } from '../date/DateHelpers';
import RangeFormatter from '../date/EventListingRangeFormatter';
import SvgIcon from '../../../utils/SvgIcon';
import IconSprites from '../../../utils/IconSprites';

const HTML = `
  <div class="${Class.CONTROLS_CONTAINER}"></div>
  <div class="${Class.RESULTS_CONTAINER}"></div>
`;

function makePerformanceListItemHtml(performance) {
  const {
    id,
    actionUrl,
    displayDate,
    displayTime,
    performanceTitle,
    isOnSale,
    performanceStatusMessage,
    actionUrlText,
    isTitleVisibleOnListView,
    productTypeName,
    productTypeId,
  } = performance;

  return `
    <li class="tn-prod-list-item__perf-list-item" data-tn-performance-no="${id}" data-tn-product-type-id="${productTypeId}">
      <a href="${actionUrl}" class="tn-prod-list-item__perf-anchor">
        ${displayDate || displayTime
    ? `<span class="tn-prod-list-item__perf-property tn-prod-list-item__perf-property--datetime">
              ${displayDate ? `<span class="tn-prod-list-item__perf-date">${displayDate}</span>` : ''}
              ${displayDate && displayTime ? '<span class="sr-only">,&nbsp;</span>' : ''}
              ${displayTime ? `<span class="tn-prod-list-item__perf-time">${displayTime}</span>` : ''}
              <span class="sr-only">,&nbsp;</span>
            </span>` : ''}
        <span class="tn-prod-list-item__perf-property tn-prod-list-item__perf-property--title">
          ${isTitleVisibleOnListView ? `<span class="tn-performance-title">${performanceTitle}</span>` : ''}
          ${productTypeName ? `
            <span class="sr-only">,&nbsp;</span>
            <span class="tn-product-type-name">${productTypeName}</span>
          ` : ''}
          <span class="sr-only">,&nbsp;</span>
        </span>
        <span class="tn-prod-list-item__perf-property tn-prod-list-item__perf-property--action">
          ${isOnSale ? `
              <span class="tn-prod-list-item__perf-action">${actionUrlText}</span>
            ` : ''}
          ${isOnSale && performanceStatusMessage ? '<span class="sr-only">,&nbsp;</span>' : ''}
          ${performanceStatusMessage ? `
              <span class="tn-prod-list-item__perf-status">${performanceStatusMessage}</span>
            ` : ''}
        </span>
        <span class="tn-prod-list-item__button btn btn-primary">
          ${SvgIcon.getHtml(IconSprites.ChevronRight, 'tn-prod-list-item__icon')}
        </span>
      </a>
    </li>
  `;
}

function makePerformanceListHtml(performances, performanceListHeadingId) {
  return `
    <ul class="list-unstyled tn-prod-list-item__property tn-prod-list-item__property--perf-list" aria-labelledby="${performanceListHeadingId}">
      ${performances.map(makePerformanceListItemHtml).join('')}
    </ul>
  `;
}

function makeProductionListItem(production) {
  const {
    productionSeasonId,
    listingImageUrl,
    productionSeasonActionUrl,
    productionTitle,
    description,
    performances,
    listingImageAlt,
    isPerformanceListVisible,
  } = production;

  const performanceListHeadingId = `tn-prod-list-item-heading-${productionSeasonId}`;

  return `
    <li data-tn-prod-season-no="${productionSeasonId}" class="tn-prod-list-item">
      <h4 id="${performanceListHeadingId}" class="tn-prod-list-item__property tn-prod-list-item__property--heading">
        <a href="${productionSeasonActionUrl}">${productionTitle}</a>
      </h4>
      <div class="tn-prod-list-item__property tn-prod-list-item__property--img-container">
        ${listingImageUrl ? `
          <a role="presentation" tabindex="-1" href="${productionSeasonActionUrl}">
            <img aria-hidden="true" role="presentation" alt="${listingImageAlt}" src="${listingImageUrl}">
          </a>` : ''
}
      </div>
      ${description ? `
        <div class="tn-prod-list-item__property tn-prod-list-item__property--description">
          ${description}
        </div>` : ''
}
      ${isPerformanceListVisible && performances ? makePerformanceListHtml(performances, performanceListHeadingId) : ''}
    </li>
  `;
}

class EventsListProductionsView {
  constructor(store, config) {
    this.store = store;
    this.config = config;

    this.$el = $('#tn-events-list-view');
    this.$el.html(HTML);
    this.$el.prepend(makeBusyIndicatorHtml());

    this.$results = this.$el.find(`.${Class.RESULTS_CONTAINER}`);
    this.$controls = this.$el.find(`.${Class.CONTROLS_CONTAINER}`);

    this.controls = new EventListControls(this.config);
    this.$controls.append(this.controls.$el);

    this.controls.on(EventListControls.UPDATE, () => {
      this.store.emit('date');
    });

    this.store.on('fetchStart', () => {
      this.$el.addClass(Class.LOADING_MODIFIER);
    });

    this.store.on('fetchEnd', () => {
      this.$el.removeClass(Class.LOADING_MODIFIER);
    });

    this.store.on('fetchFailed', () => {
      this.$el.removeClass(Class.LOADING_MODIFIER);
      this.$results.html('');
    });

    this.$results.html(makeResultsDateRangeHeading(this.getResultsHeadingText()));
  }

  getSelectedDateFormatted() {
    const { startDate, endDate } = this.getRangeForSelectedDate();

    return RangeFormatter.range(startDate, endDate);
  }

  setData(data) {
    this.renderResults(data);
  }

  getResultsHeadingText() {
    if (this.config.hideListViewResultsHeading === true) {
      return '';
    }

    return this.getSelectedDateFormatted();
  }

  renderResults({ productions }) {
    const { startDate, endDate } = this.getRangeForSelectedDate();
    const filteredProductions = [];

    productions.forEach((production) => {
      const filteredPerformances = production.performances
        .filter((perf) => isBetweenDates(perf.iso8601Date, startDate, endDate));

      if (filteredPerformances.length > 0) {
        filteredProductions.push({ ...production, performances: filteredPerformances });
      }
    });

    if (filteredProductions.length) {
      const listItems = filteredProductions.map(makeProductionListItem).join('');

      const resultsHeadingId = 'tn-event-listing-view-results-heading';

      this.$results.html(`
          ${makeResultsDateRangeHeading(this.getResultsHeadingText(), resultsHeadingId)}
          <ul class="list-unstyled" aria-labelledby="${resultsHeadingId}">${listItems}</ul>
        `);
    } else {
      this.$results.html(makeNoResultsHtml('', this.getSelectedDateFormatted()));
    }
  }

  getRangeForSelectedDate() {
    return this.controls.getSearchDateRange();
  }
}

export default EventsListProductionsView;
